import RestaurantKitchenOrderService from '../../../services/restaurant/RestaurantKitchenOrderService';
import RestaurantOrderService from '../../../services/restaurant/RestaurantOrderService';
import RestaurantTableService from '../../../services/restaurant/RestaurantTableService';
import { setKitchenOrders } from '../../../store/slices/restaurant/restaurantKitchenOrderSlice';
import { setRestaurantTables } from '../../../store/slices/restaurant/restaurantTableSlice';
import { AppDispatch } from '../../../store/store';

const STORAGE_KEY = 'restaurant-billing-context';

export interface RestaurantBillingContext {
  orderId: number;
  saleId: number;
  branchId: number;
  tableId: number;
}

export const saveRestaurantBillingContext = (
  context: RestaurantBillingContext
) => {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
};

export const getRestaurantBillingContext =
  (): RestaurantBillingContext | null => {
    const value = sessionStorage.getItem(STORAGE_KEY);
    if (!value) return null;

    try {
      return JSON.parse(value) as RestaurantBillingContext;
    } catch {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
  };

export const clearRestaurantBillingContext = () => {
  sessionStorage.removeItem(STORAGE_KEY);
};

export const refreshRestaurantAfterSaleFinalized = async (
  saleId: number,
  dispatch: AppDispatch
) => {
  const context = getRestaurantBillingContext();
  if (!context || context.saleId !== saleId) return null;

  const [orderResult, activeOrdersResult, tablesResult, kitchenResult] =
    await Promise.allSettled([
      RestaurantOrderService.get(context.orderId),
      RestaurantOrderService.list(context.branchId),
      RestaurantTableService.list(context.branchId, true),
      RestaurantKitchenOrderService.list(context.branchId, {
        stationId: '',
        status: '',
      }),
    ]);

  if (tablesResult.status === 'fulfilled') {
    dispatch(setRestaurantTables(tablesResult.value.data ?? []));
  }

  if (kitchenResult.status === 'fulfilled') {
    dispatch(setKitchenOrders(kitchenResult.value.data ?? []));
  }

  const closedOrder =
    orderResult.status === 'fulfilled' &&
    orderResult.value.data.status === 'CLOSED';
  const activeOrdersRefreshed = activeOrdersResult.status === 'fulfilled';

  if (closedOrder && activeOrdersRefreshed) {
    clearRestaurantBillingContext();
  }

  return {
    context,
    closedOrder,
    activeOrdersRefreshed,
    tablesRefreshed: tablesResult.status === 'fulfilled',
    kitchenRefreshed: kitchenResult.status === 'fulfilled',
  };
};
